import React from 'react';
import {
  BellOff,
  CheckCheck,
  DatabaseZap,
  CheckCircle,
  XCircle,
  Sparkles,
  Tag,
  Newspaper,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Notification, NotificationType } from '../api/types';
import { useNotifications, useMarkAllRead } from '../api/notifications';
import { apiClient } from '../lib/api-client';

const typeIcon: Record<NotificationType, React.ReactNode> = {
  table_update: <DatabaseZap className="w-4 h-4 text-blue-500" />,
  import_ready: <CheckCircle className="w-4 h-4 text-green-500" />,
  import_confirmed: <CheckCircle className="w-4 h-4 text-green-500" />,
  import_failed: <XCircle className="w-4 h-4 text-red-500" />,
  import_cancelled: <XCircle className="w-4 h-4 text-slate-400" />,
  bootstrap_confirmed: <Sparkles className="w-4 h-4 text-purple-500" />,
  promotion: <Tag className="w-4 h-4 text-orange-500" />,
  news: <Newspaper className="w-4 h-4 text-slate-500" />,
};

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours}h`;
  return `hace ${Math.floor(hours / 24)} días`;
}

type Props = {
  onClose: () => void;
};

export default function NotificationDropdown({ onClose }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useNotifications();
  const markAllRead = useMarkAllRead();

  const notifications = data?.data ?? [];
  const hasUnread = notifications.some(n => !n.readAt);

  const handleClick = (n: Notification) => {
    if (!n.readAt) {
      void apiClient.patch(`/notifications/${n.id}/read`, {}).then(() => {
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      });
    }
    onClose();
    // `assign` navega igual que asignar `location.href`, pero es una llamada y
    // no una mutación, que es lo que la regla de inmutabilidad marcaba.
    if (n.actionUrl) window.location.assign(n.actionUrl);
  };

  return (
    <div
      className="absolute right-0 z-50 mt-2 bg-white border shadow-xl rounded-xl border-pale-grey-three w-80"
      role="dialog"
      aria-label="Notificaciones"
    >
      <div className="flex items-center justify-between p-4 border-b border-pale-grey-three">
        <span className="text-sm font-semibold text-charcoal-grey">Notificaciones</span>
        {hasUnread && (
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
          >
            <CheckCheck className="w-3 h-3" />
            Marcar todo leído
          </button>
        )}
      </div>

      <div className="overflow-y-auto max-h-72">
        {isLoading && <div className="p-4 text-sm text-center text-cool-grey">Cargando...</div>}
        {!isLoading && notifications.length === 0 && (
          <div className="p-6 text-sm text-center text-cool-grey">
            <BellOff className="w-8 h-8 mx-auto mb-2 text-pale-grey-three" />
            No tienes notificaciones
          </div>
        )}
        {notifications.map(n => (
          <button
            key={n.id}
            type="button"
            className={`w-full text-left p-3 border-b border-pale-grey-three last:border-0 hover:bg-pale-grey transition flex gap-3 items-start ${
              !n.readAt ? 'bg-blue-50/40' : ''
            }`}
            onClick={() => handleClick(n)}
          >
            <span className="mt-0.5 shrink-0">{typeIcon[n.type]}</span>
            <div className="min-w-0 flex-1">
              <p
                className={`text-xs leading-snug ${
                  !n.readAt ? 'font-semibold text-charcoal-grey' : 'font-medium text-slate'
                }`}
              >
                {n.title}
              </p>
              {n.body ? (
                <p className="mt-0.5 text-xs text-cool-grey line-clamp-2">{n.body}</p>
              ) : null}
              <p className="mt-1 text-xs text-pale-grey-three">{relativeTime(n.createdAt)}</p>
            </div>
            {!n.readAt && <span className="w-2 h-2 mt-1 rounded-full bg-primary shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
}
